import { Badge, Text } from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectExcelDetailsList,
  selectLoading,
  selectOrderBy,
  selectOrderDir,
  selectPageNo,
  selectPageSize,
  selectSearch,
  selectTotalRow,
} from "./redux/selector";

import { useTranslation } from "react-i18next";
import { MdChevronLeft } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import CustomButton from "../../../components/Common/CustomButton";
import DateFormat from "../../../components/Common/DateFormat";
import { CommonTable } from "../../../components/CommonTable";
import { ExcelDetailsStatusData, excelDetailStatusColorMap } from "./data";
import { actions } from "./redux/slice";
import { IExcelDetailsData } from "./types";

const ExcelList = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const pageNo = useSelector(selectPageNo);
  const pageSize = useSelector(selectPageSize);
  const search = useSelector(selectSearch);
  const tableData = useSelector(selectExcelDetailsList);
  const loading = useSelector(selectLoading);
  const total = useSelector(selectTotalRow);
  const orderBy = useSelector(selectOrderBy);
  const orderDir = useSelector(selectOrderDir);

  React.useEffect(() => {
    if (id) dispatch(actions.doGetExcelListById(id));
    return () => {};
  }, [id, pageNo, pageSize, search, orderDir, orderBy]);

  const columnHelper = createColumnHelper<IExcelDetailsData>();
  const columns = [
        columnHelper.accessor("_id", {
      id: "_id",
      header: `${t("common.list.sNo")}`,
      cell: (info) => {
        return (
          <>
            {pageNo === 1 ? (
              <>{info.row.index + 1}</>
            ) : (
              <>{`${(pageNo - 1) * pageSize + info.row.index + 1}`}</>
            )}
          </>
        );
      },
    }),
    columnHelper.accessor("title", {
      id: "title",
      header: `${t("common.list.title")}`,
      cell: (info) => <Text>{info.getValue() || "NA"}</Text>,
    }),
    columnHelper.accessor("price", {
      id: "price",
      header: `${t("common.list.price")}`,
      cell: (info) => <Text>{info.getValue() || "NA"}</Text>,
    }),
    columnHelper.accessor("taxRate", {
      id: "taxRate",
      header: `${t("common.list.taxRate")}`,
      cell: (info) => <Text>{info.getValue() || "NA"}</Text>,
    }),
    columnHelper.accessor("category", {
      id: "category",
      header: `${t("common.list.category")}`,
      cell: (info) => <Text>{info.getValue() || "NA"}</Text>,
    }),


    columnHelper.accessor("status", {
      id: "status",
      header: `${t("common.list.status")}`,
      cell: (info) => {
        const statusValue = info.getValue();
        const label = ExcelDetailsStatusData[statusValue] || "Unknown";
        const color = excelDetailStatusColorMap[statusValue] || "gray";

        return (
          <Badge
            colorScheme={color}
            fontSize="md"
            fontWeight="500"
            sx={{ textTransform: "capitalize" }}
          >
            {t(label)}
          </Badge>
        );
      },
    }),

    columnHelper.accessor("error", {
      id: "error",
      header: `${t("common.list.error")}`,
      cell: (info) => <Text>{info.getValue().map((error) =>  "Filed:" + error.field + " :- Message: " + error.message).join(", ") || "NA"}</Text>,
    }),

    columnHelper.accessor("createdAt", {
      id: "createdAt",
      header: `${t("common.list.createdAt")}`,
      cell: (info) => <Text>{DateFormat(info.getValue()) || "NA"}</Text>,
       sortDescFirst: true,
    }),
    columnHelper.accessor("updatedAt", {
      id: "updatedAt",
      header: `${t("common.list.updatedAt")}`,
      cell: (info) => <Text>{DateFormat(info.getValue()) || "NA"}</Text>,
    }),
  ];

  return (
    <>
      <CommonTable<IExcelDetailsData>
        columns={columns}
        data={tableData}
        totalCount={total}
        pageNo={pageNo}
        pageSize={pageSize}
        search={search}
        loading={loading}
        onSearch={(v) => dispatch(actions.setSearch(v))}
        onPageChange={(p) => dispatch(actions.setPageNo(p))}
        onPageSizeChange={(s) => dispatch(actions.setPageSize(s))}
        onSortChange={(id, desc) => {
          dispatch(actions.setOrderBy(id));
          dispatch(actions.setOrderDir(desc ? "desc" : "asc"));
        }}
        hideAddButton
        filters={
          <CustomButton
            handleClick={() => {
              navigate(-1);
            }}
            title={t("common.button.back")}
            variant="brand"
            leftIcon={<MdChevronLeft />}
            isLeftIcon
          />
        }
      />
    </>
  );
};

export default ExcelList;
